import { useCallback, useEffect, useState } from 'react';

import { SUBSCRIPTION_REQUIRED_EVENT, type SubscriptionRequiredEventDetail } from '../lib/api';
import { normalizeAccessMode, type AccessMode, type PaywallOrigin } from './useSubscriptionGate';

export interface SubscriptionPaywallState {
  open: boolean;
  origin?: PaywallOrigin;
  accessMode: AccessMode;
}

const CLOSED: SubscriptionPaywallState = { open: false, accessMode: 'FREE' };

/**
 * Hook global que escuta o evento `subscription-required` — disparado pelo
 * `apiClient` em 403 `SUBSCRIPTION_REQUIRED` e pelo `useSubscriptionGate` no
 * clique de um recurso pago. Monta um único listener por App (renderizado uma
 * vez no root); o `SubscriptionPaywall` é controlado pelo estado retornado.
 *
 * Espelha `useInsufficientCreditsHandler` (402 / créditos).
 */
export function useSubscriptionRequiredHandler() {
  const [state, setState] = useState<SubscriptionPaywallState>(CLOSED);

  useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<SubscriptionRequiredEventDetail>;
      setState({
        open: true,
        origin: custom.detail?.origin as PaywallOrigin | undefined,
        accessMode: normalizeAccessMode(custom.detail?.accessMode),
      });
    };
    window.addEventListener(SUBSCRIPTION_REQUIRED_EVENT, handler as EventListener);
    return () => {
      window.removeEventListener(SUBSCRIPTION_REQUIRED_EVENT, handler as EventListener);
    };
  }, []);

  const closeSubscriptionPaywall = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  return { state, closeSubscriptionPaywall };
}
